import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

import { ConfirmationModalComponent } from '../../../components/modals/confirmation-modal/confirmation-modal.component';
import { TContextGroup } from '@core/api/context/types';
import { ListItemComponent } from '@shared/components/content-panel';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { CommonService } from '@shared/services/common.service';
import { ContextService } from '@shared/services/context.service';

import { ContextGroupFormModalComponent } from '../context-group-form-modal/context-group-form-modal.component';

interface GroupTreeNode extends TContextGroup {
  children: GroupTreeNode[];
  expanded: boolean;
}

@Component({
  selector: 'app-context-groups-list',
  templateUrl: './context-groups-list.component.html',
  styleUrl: './context-groups-list.component.scss',
  imports: [
    MatIcon,
    MatMenuModule,
    MatTooltip,
    SpinnerComponent,
    ListItemComponent,
  ],
})
export class ContextGroupsListComponent implements OnInit, OnDestroy {
  @Output() groupSelected = new EventEmitter<TContextGroup>();

  groupsTree: GroupTreeNode[] = [];
  selectedGroup: TContextGroup | null = null;
  loading = false;

  private _subs: Subscription[] = [];

  constructor(
    private _contextService: ContextService,
    private _commonService: CommonService,
    private _dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this._subs.push(
      this._contextService.groups$.subscribe(groups => {
        this.groupsTree = this.buildTree(groups);

        // Auto-select first group if none is selected and groups exist
        if (!this.selectedGroup && this.groupsTree.length > 0) {
          this.selectGroup(this.groupsTree[0]);
        }
      })
    );

    this._subs.push(
      this._contextService.groupsLoading$.subscribe(loading => {
        this.loading = loading;
      })
    );

    this._subs.push(
      this._contextService.selectedGroup$.subscribe(group => {
        this.selectedGroup = group;
      })
    );

    this._contextService.fetchGroups();
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  private buildTree(groups: TContextGroup[]): GroupTreeNode[] {
    const rootGroups = groups
      .filter(g => !g.parentGroup)
      .sort((a, b) => a.name.localeCompare(b.name));

    return rootGroups.map(root => ({
      ...root,
      expanded: true,
      children: groups
        .filter(g => {
          const parentId = typeof g.parentGroup === 'string' ? g.parentGroup : g.parentGroup?._id;
          return parentId === root._id;
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(child => ({
          ...child,
          expanded: false,
          children: [],
        })),
    }));
  }

  selectGroup(group: TContextGroup): void {
    // group.selected = true;
    this._contextService.setSelectedGroup(group);
    this.groupSelected.emit(group);
  }

  toggleExpand(node: GroupTreeNode, event: Event): void {
    event.stopPropagation();
    node.expanded = !node.expanded;
  }

  openCreateGroupModal(parentGroup?: TContextGroup): void {
    const dialogRef = this._dialog.open(ContextGroupFormModalComponent, {
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      data: { parentGroup },
    });

    dialogRef.componentInstance.groupSaved.subscribe((group: TContextGroup) => {
      this._commonService.openSuccessSnackBar(`Group "${group.name}" created successfully`);
      dialogRef.close();
    });
  }

  openEditGroupModal(group: TContextGroup, _event: Event): void {
    // Do not call stopPropagation on the menu item click — it keeps the overlay menu open behind the dialog.
    const dialogRef = this._dialog.open(ContextGroupFormModalComponent, {
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      data: { group, isEdit: true },
    });

    dialogRef.componentInstance.groupSaved.subscribe((updated: TContextGroup) => {
      this._commonService.openSuccessSnackBar(`Group "${updated.name}" updated successfully`);
      dialogRef.close();
    });
  }

  openDeleteGroupModal(group: TContextGroup, _event: Event): void {
    // Do not call stopPropagation on the menu item click — same as openEditGroupModal.

    // Check if group has children (only root groups can have children in this tree structure)
    const hasChildren = this.groupsTree.some(root =>
      root._id === group._id && root.children && root.children.length > 0
    );

    if (hasChildren) {
      this._commonService.openErrorSnackBar('Cannot delete group with child groups. Delete children first.');
      return;
    }

    const dialogRef = this._dialog.open(ConfirmationModalComponent, {
      data: { text: `Are you sure you want to delete "${group.name}"? This will also delete all items in this group.` },
    });

    dialogRef.componentInstance.actionCallback.subscribe(() => {
      this._contextService.deleteGroup(group._id, () => {
        this._commonService.openSuccessSnackBar(`Group "${group.name}" deleted successfully`);
        dialogRef.close();
      });
    });
  }

  toggleApproval(group: TContextGroup, event: Event): void {
    event.stopPropagation();
    const newApproval = !group.approved;
    this._contextService.setGroupApproval(group._id, newApproval, () => {
      this._commonService.openSuccessSnackBar(
        `Group "${group.name}" ${newApproval ? 'approved' : 'unapproved'} successfully`
      );
    });
  }

  regenerateEmbeddings(group: TContextGroup, event: Event): void {
    event.stopPropagation();
    this._contextService.regenerateGroupEmbeddings(group._id, response => {
      this._commonService.openSuccessSnackBar(response.message || 'Embeddings regenerated successfully');
    });
  }

  isSelected(group: TContextGroup): boolean {
    return this.selectedGroup?._id === group._id;
  }

  getStatusClass(group: TContextGroup): string {
    if (!group.approved) return 'status-unapproved';
    return group.status === 'active' ? 'status-active' : 'status-inactive';
  }
}

